import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Globe2, Microscope, Sparkles } from "lucide-react";

const HERO_IMAGE =
  "https://images.pexels.com/photos/9574573/pexels-photo-9574573.jpeg";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-white">
      {/* Right-side hero image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden
      />
      <div className="absolute inset-0 hero-gradient-overlay" aria-hidden />
      <div className="absolute inset-0 bg-lab-grid opacity-40" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.a
              href="/color"
              data-testid="hero-color-announcement"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="group inline-flex items-center gap-2.5 mb-6 bg-white/80 backdrop-blur border border-teal-200 hover:border-teal-500 pl-1.5 pr-3.5 py-1.5 transition-colors max-w-full"
            >
              <span className="inline-flex items-center gap-1 bg-teal-700 text-white text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-1">
                <Sparkles className="w-3 h-3 animate-pulse" strokeWidth={2} />
                New
              </span>
              <span className="text-xs sm:text-sm text-slate-700 leading-snug">
                Color is sometimes a <strong className="text-slate-900">Critical Quality Attribute</strong> — measure it online. Access to further details provided on request.
              </span>
              <ArrowRight className="w-4 h-4 text-teal-700 shrink-0 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
            </motion.a>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow mb-6"
            >
              Global Pharmaceutical Services
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]"
            >
              Precision-Driven partners in your Business Development Journey.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-7 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed"
            >
              Xpertee Pharma Services delivers end-to-end{" "}
              <strong className="text-slate-800 font-semibold">API & raw material procurement</strong>,{" "}
              <strong className="text-slate-800 font-semibold">GMP & regulatory excellence</strong>,{" "}
              <strong className="text-slate-800 font-semibold">advanced characterization</strong> and{" "}
              <strong className="text-slate-800 font-semibold">microbiological, stability & analytical testing</strong>{" "}
              — engineered for regulators, trusted by manufacturers.
              <span className="block mt-4 text-teal-700 font-semibold">
                Your one-stop shop for all your business development needs.
              </span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center bg-teal-700 hover:bg-teal-800 text-white px-8 py-4 text-sm font-medium transition-colors"
              >
                Request a Consultation
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center bg-white text-teal-700 border border-teal-200 hover:border-teal-700 hover:bg-teal-50 px-8 py-4 text-sm font-medium transition-colors"
              >
                Explore Our Services
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl"
            >
              <Stat icon={ShieldCheck} value="US FDA · EU EMA" label="Regulatory alignment" />
              <Stat icon={Globe2} value="40+ Countries" label="Global sourcing network" />
              <Stat icon={Microscope} value="ISO 17025" label="Accredited testing" />
            </motion.div>
          </div>

          {/* Reserved space for the hero visual — image is background */}
          <div className="hidden lg:block lg:col-span-5" />
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-start gap-3 border-l border-teal-200 pl-4">
      <Icon className="w-5 h-5 text-teal-700 mt-0.5" strokeWidth={1.75} />
      <div>
        <div className="font-display text-sm font-semibold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}
